/**
 * Adapted from https://github.com/vunguyentuan/react-virtual-slider
 * Code copied in here and updated for better React 16.3+ compatibility.
 * Was adopted from the original project at version 1.0.3.
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { getSizes, getItemPerPage, getAnimatingOffset } from './vs-utils';

class VirtualList extends PureComponent {
  constructor(props) {
    super(props);

    this.container = React.createRef();
    this.list = React.createRef();

    this.state = {
      itemPerPage: 0,
      from: 0,
      viewportWidth: 0,
    };
  }

  componentDidMount() {
    this.calculateItemPerPage(this.props, this.container);

    window.addEventListener(
      'resize',
      this.calculateItemPerPage.bind(null, this.props, this.container)
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'resize',
      this.calculateItemPerPage.bind(null, this.props, this.container)
    );
  }

  scrollTo = (index) => {
    const { startAction, endAction, length } = this.props;
    const { itemPerPage } = this.state;

    if (index === 0 && startAction) {
      startAction();
    }

    if (index === length - itemPerPage && endAction) {
      endAction();
    }

    if (index === this.state.from) return;

    this.setState({
      from: index,
    });
    this.list.current.removeEventListener('transitionend', this.lastTransitionendAction);
    this.lastTransitionendAction = null;
  };

  calculateItemPerPage = (props, container) => {
    const { itemSize } = props;

    // If for some reason container is null (seems to happen on remounts), shortcircuit
    if (!container.current) return;

    const viewportWidth = container.current.offsetWidth;
    const itemPerPage = getItemPerPage(viewportWidth, itemSize);

    this.setState({
      itemPerPage,
      viewportWidth,
    });
  };

  goBack = () => {
    const { length, itemSize } = this.props;
    const { itemPerPage, from, viewportWidth } = this.state;
    const prevValue = Math.max(from - itemPerPage, 0);
    const nextStyle = getSizes(itemSize, itemPerPage, prevValue, length, viewportWidth);
    const listEl = this.list.current;

    listEl.style.transform = 'translateX(0px)';
    listEl.style.transition = 'transform 0.5s ease';
    const doSetState = () => {
      listEl.style.transition = 'none';
      listEl.style.transform = `translateX(-${nextStyle.offsetLeft}px)`;
      this.scrollTo(prevValue);
    };

    if (this.lastTransitionendAction) {
      listEl.removeEventListener('transitionend', this.lastTransitionendAction);
    }
    listEl.addEventListener('transitionend', doSetState);
    this.lastTransitionendAction = doSetState;
  };

  goNext = () => {
    const { length, itemSize } = this.props;
    const { itemPerPage, from, viewportWidth } = this.state;
    const nextStart = Math.min(from + itemPerPage, length - itemPerPage);
    const listEl = this.list.current;

    const nextStyle = getSizes(itemSize, itemPerPage, nextStart, length, viewportWidth);
    const animatingOffset = getAnimatingOffset(
      itemSize,
      itemPerPage,
      from,
      length,
      viewportWidth,
      nextStart
    );

    listEl.style.transform = `translateX(-${animatingOffset}px)`;
    listEl.style.transition = 'transform 0.5s ease';
    const doSetState = () => {
      listEl.style.transition = 'none';
      listEl.style.transform = `translateX(-${nextStyle.offsetLeft}px)`;
      this.scrollTo(nextStart);
    };

    if (this.lastTransitionendAction) {
      listEl.removeEventListener('transitionend', this.lastTransitionendAction);
    }
    listEl.addEventListener('transitionend', doSetState);
    this.lastTransitionendAction = doSetState;
  };

  render() {
    const {
      length, itemSize, itemRenderer, containerStyle,
    } = this.props;
    const { itemPerPage, from, viewportWidth } = this.state;

    const { end, offsetLeft, start } = getSizes(itemSize, itemPerPage, from, length, viewportWidth);

    const style = {
      width: itemSize * length,
      transform: `translateX(-${offsetLeft}px)`,
      willChange: 'transform',
    };

    const items = [];
    for (let i = start; i < end; i += 1) {
      items.push(itemRenderer(i, i));
    }

    return (
      <div className="slider-container" style={containerStyle} ref={this.container}>
        <div style={style} ref={this.list}>
          {items}
        </div>
      </div>
    );
  }
}

VirtualList.propTypes = {
  length: PropTypes.number.isRequired,
  itemSize: PropTypes.number.isRequired,
  itemRenderer: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  containerStyle: PropTypes.object.isRequired,
  startAction: PropTypes.func,
  endAction: PropTypes.func,
};

VirtualList.defaultProps = {
  startAction: null,
  endAction: null,
};

export default VirtualList;



// WEBPACK FOOTER //
// ./src/components/common/VirtualSlider/VirtualSliderComponents.jsx